<template>
  <div class="page-container news-list-page">
    <div class="page-header">
      <div class="container">
        <h1>{{ $t('pages.news_list_title') }}</h1>
        <p class="breadcrumb">
          <router-link to="/">{{ $t('index.latest_news') }}</router-link> /
          <span>{{ $t('pages.news_list_title') }}</span>
        </p>
      </div>
    </div>

    <div class="page-content container">
      <el-row :gutter="30">
        <!-- 左侧：新闻列表 -->
        <el-col :span="18" :xs="24">
          <div class="list-wrapper">
            <div v-if="loading" class="loading-placeholder">
              <el-skeleton :rows="6" animated />
            </div>
            <div v-else-if="newsList.length === 0" class="empty-data">
              <el-empty :description="$t('common.no_data')" />
            </div>
            <div v-else>
              <div v-for="item in newsList" :key="item.id" class="news-item">
                <div class="item-image" v-if="item.cover_image">
                  <img :src="item.cover_image" :alt="item.title" class="lazy-img">
                </div>
                <!-- 无封面时占位置 -->
                <div class="item-image placeholder" v-else>
                  <div class="no-image">{{ $t('common.no_image') }}</div>
                </div>
                <div class="item-content">
                  <div
                    class="news-title-link"
                    @click="handleNewsClick(item)"
                    style="cursor: pointer;"
                  >
                    <h3>{{ item.title }}</h3>
                  </div>
                  <p class="summary" v-if="item.summary">{{ item.summary }}</p>
                  <div class="meta">
                    <span class="category">{{ item.category.name }}</span>
                    <span class="time">
                      {{ $t('pages.publish_time') }}: {{ formatTime(item.publish_time) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 分页：适配接口的 count/current 格式 -->
          <div v-if="!loading && totalPages > 1" class="pagination-wrapper">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="totalCount"
              :page-size="pageSize"
              :current-page="currentPage"
              :page-count="totalPages"
              :disabled="loading"
              @current-change="handlePageChange"
            />
          </div>
        </el-col>

        <!-- 右侧：侧边栏（分类、热门） -->
        <el-col :span="6" :xs="24" class="sidebar-col">
          <div class="sidebar">
            <div class="sidebar-block">
              <h4>{{ $t('pages.news_list_title') }}分类</h4>
              <ul v-if="!categoryLoading">
                <!-- 全部分类选项 -->
                <li :class="{ active: currentCategory === null }">
                  <a href="#" @click.prevent="selectCategory(null)">{{ $t('common.all') }}</a>
                </li>
                <!-- 接口加载的分类 -->
                <li
                  v-for="cat in flatCategories"
                  :key="cat.id"
                  :class="{ active: cat.id === currentCategory }"
                >
                  <a href="#" @click.prevent="selectCategory(cat.id)">{{ cat.name }}</a>
                </li>
              </ul>
              <el-skeleton :rows="5" animated v-else />
            </div>
            <!--            <div class="sidebar-block">-->
            <!--              <h4>{{ $t('pages.hot_news') }}</h4>-->
            <!--              &lt;!&ndash; 预留热门新闻接口，暂时保留模拟数据，可后续替换 &ndash;&gt;-->
            <!--              <ul>-->
            <!--                <li v-for="hot in hotNews" :key="hot.id">-->
            <!--                  <router-link :to="`/news/${hot.id}`">{{ hot.title }}</router-link>-->
            <!--                </li>-->
            <!--              </ul>-->
            <!--            </div>-->
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns'; // 可选：用于时间格式化，需安装 npm i date-fns
import { getNewsListApi, getNewsCategoryApi, getNewsDetailApi } from '@/assets/api/website';
import { useRoute, useRouter } from 'vue-router';

// -------------------------- 类型定义 --------------------------
/** 分类项类型（适配接口返回的 CategoryTreeSchema） */
interface CategoryItem {
  id: number;
  name: string;
  pid: number;
  level: number;
  sort: number;
  children?: CategoryItem[];
}

/** 新闻分类下的子项类型 */
interface NewsCategory {
  id: number;
  name: string;
}

/** 新闻列表项类型 */
interface NewsListItem {
  id: number;
  title: string;
  summary?: string;
  cover_image?: string;
  publish_time: string | Date;
  category: NewsCategory;
}

/** 新闻列表接口返回类型 - 新版：count/current 格式 */
interface NewsListResponse {
  list: NewsListItem[];
  count: number;  // 总页数
  current: number; // 当前页码
}

/** 新闻详情类型（预留） */
interface NewsDetail extends NewsListItem {
  content?: string;
  source?: string;
  author?: string;
  hit_count: number;
}

// -------------------------- 状态管理 --------------------------
const { t, locale } = useI18n();
const router = useRouter();
// 加载状态
const loading = ref(true);
const categoryLoading = ref(true);

// 新闻列表数据
const newsList = ref<NewsListItem[]>([]);
// 分页相关
const pageSize = ref(10);
const currentPage = ref(1);
const totalPages = ref(0); // 总页数
const totalCount = ref(0); // 总条数（通过总页数 * 每页大小估算）
const currentCategory = ref<number | null>(null);

// 分类数据
const categories = ref<CategoryItem[]>([]);
// 扁平化的分类列表（处理树形结构）
const flatCategories = computed(() => {
  const flatten = (list: CategoryItem[]): CategoryItem[] => {
    return list.reduce((acc, item) => {
      acc.push(item);
      if (item.children && item.children.length) {
        acc.push(...flatten(item.children));
      }
      return acc;
    }, [] as CategoryItem[]);
  };
  return flatten(categories.value);
});

// -------------------------- 方法定义 --------------------------
/** 格式化时间 */
const formatTime = (time: string | Date) => {
  if (!time) return '';
  // 可根据需求调整格式，需安装 date-fns 或使用原生 Date
  try {
    return format(new Date(time), 'yyyy-MM-dd HH:mm');
  } catch (e) {
    return time.toString();
  }
};

/** 获取新闻分类 */
const fetchCategories = async () => {
  categoryLoading.value = true;
  try {
    const res = await getNewsCategoryApi({ lang: locale.value });
    console.log('分类数据:', res);
    categories.value = res as CategoryItem[];
  } catch (error) {
    console.error('获取新闻分类失败:', error);
    categories.value = [];
  } finally {
    categoryLoading.value = false;
  }
};

/** 获取新闻列表 */
const fetchNewsList = async () => {
  loading.value = true;
  try {
    const res = await getNewsListApi({
      lang: locale.value,
      page: currentPage.value,
      size: pageSize.value,
      category_id: currentCategory.value,
    }) as NewsListResponse;

    console.log('新闻列表数据:', res);

    newsList.value = res.list;
    totalPages.value = res.count; // 总页数
    currentPage.value = res.current; // 当前页码

    // 计算总条数（用于分页组件显示，使用估算值）
    totalCount.value = totalPages.value * pageSize.value;

  } catch (error) {
    console.error('获取新闻列表失败:', error);
    newsList.value = [];
    totalPages.value = 0;
    totalCount.value = 0;
  } finally {
    loading.value = false;
  }
};

/** 分页切换 */
const handlePageChange = (page: number) => {
  currentPage.value = page;
  fetchNewsList();
  // 滚动到顶部
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

/** 分类选择 */
const selectCategory = (categoryId: number | null) => {
  currentCategory.value = categoryId;
  currentPage.value = 1; // 切换分类重置页码
  fetchNewsList();
};
/** 打开新闻 **/
// <script setup lang="ts"> 中新增
const handleNewsClick = (item: any) => {
  // 检查外部链接是否存在（非空、非空字符串）
  if (item.external_link && item.external_link.trim() !== '') {
    // 打开外部链接（_blank 表示新标签页，也可改为 _self 在当前页打开）
    window.open(item.external_link, '_blank');
  } else {
    // 内部路由跳转（使用 vue-router 的 push 方法）
    router.push(`/news/${item.id}`);
  }
};
// -------------------------- 计算属性 --------------------------
// 是否需要显示分页（总页数大于1时才显示）
const showPagination = computed(() => {
  return !loading.value && totalPages.value > 1;
});

// -------------------------- 监听与初始化 --------------------------
// 监听语言变化，重新加载数据
watch(
  () => locale.value,
  () => {
    fetchCategories();
    fetchNewsList();
  },
  { immediate: true }
);

// 初始化
// onMounted(() => {
//   fetchCategories();
//   fetchNewsList();
// });
</script>

<style scoped lang="scss">
@import '@/assets/css/common.scss';

.news-list-page {
  .page-header {
    background-color: #f5f5f5;
    padding: 100px 0 40px;

    h1 {
      color: $primary-color;
      margin-bottom: 10px;
    }

    .breadcrumb {
      font-size: 14px;
      color: #999;

      a {
        color: #666;
        text-decoration: none;

        &:hover {
          color: $primary-color;
        }
      }

      span {
        color: $primary-color;
      }
    }
  }

  .page-content {
    padding: 50px;
  }

  .list-wrapper {
    min-height: 500px;
  }

  .news-item {
    display: flex;
    margin-bottom: 30px;
    padding-bottom: 30px;
    border-bottom: 1px solid #eee;
    transition: box-shadow 0.3s;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    }

    .item-image {
      flex-shrink: 0;
      width: 200px;
      height: 150px;
      overflow: hidden;
      border-radius: 8px;
      margin-right: 20px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s;
      }

      &.placeholder {
        background-color: #f5f5f5;
        display: flex;
        align-items: center;
        justify-content: center;

        .no-image {
          color: #999;
          font-size: 14px;
        }
      }
    }

    &:hover .item-image img {
      transform: scale(1.05);
    }

    .item-content {
      flex-grow: 1;

      h3 {
        font-size: 20px;
        margin-top: 0;
        margin-bottom: 10px;
        color: #333;
        transition: color 0.3s;

        &:hover {
          color: $primary-color;
        }
      }

      .summary {
        color: #666;
        line-height: 1.6;
        margin-bottom: 15px;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .meta {
        display: flex;
        gap: 20px;
        font-size: 13px;
        color: #999;

        .category {
          color: $primary-color;
          font-weight: bold;
        }
      }
    }
  }

  .pagination-wrapper {
    text-align: center;
    margin-top: 30px;
  }

  .sidebar {
    padding: 20px;
    background-color: #f9f9f9;
    border-radius: 8px;

    .sidebar-block {
      margin-bottom: 30px;

      h4 {
        font-size: 18px;
        color: $primary-color;
        border-bottom: 2px solid $primary-color;
        padding-bottom: 10px;
        margin-bottom: 15px;
      }

      ul {
        list-style: none;
        padding: 0;

        li {
          margin-bottom: 10px;
          font-size: 14px;

          a {
            color: #666;
            text-decoration: none;
            transition: color 0.3s;

            &:hover {
              color: $primary-color;
            }
          }

          &.active a {
            color: $primary-color;
            font-weight: bold;
          }
        }
      }
    }
  }

  // 响应式调整
  @media (max-width: 768px) {
    .news-item {
      flex-direction: column;

      .item-image {
        width: 100%;
        height: 200px;
        margin-right: 0;
        margin-bottom: 15px;
      }
    }

    .sidebar-col {
      margin-top: 30px;
    }
  }
}
</style>